/* eslint-disable complexity */

import Padding from "../shared/Padding";
import type { DailyHeadlineSubmissionPost, HeadlineComment } from "@/local-shared/returnTypes";
import CloseIcon from "@/assets/img/symbolic/close-thin.svg?react";
import RealizeLoader from "@/assets/img/icons/animated-r.svg?react";
import DailyHeadlineComment from "./DailyHeadlineComment";
import { useContext, useEffect, useRef, useState } from "react";
import social from "@/api/social";
import { MS_IN_S } from "@/realize-shared/model/Time";
import { AuthContext } from "@/Realize";
import Swipeable from "../Swipeable";
import { Keyboard } from "@capacitor/keyboard";
import React from "react";
import { twMerge } from "tailwind-merge";
import PopUpLogin from "../PopUpLogin";

function mergeComments(
    startComments: HeadlineComment[],
    newComments: HeadlineComment[],
)
{
    const mergedComments = [...startComments];

    newComments.forEach((newComment) =>
    {
        const existingCommentIndex = mergedComments.findIndex((comment) => comment.id === newComment.id);

        if (existingCommentIndex !== -1)
        {
            mergedComments[existingCommentIndex] = newComment;
        }
        else
        {
            mergedComments.push(newComment);
        }
    });


    // Order by id descending
    mergedComments.sort((a, b) =>
        b.id - a.id);

    return mergedComments;
}

const CommentInputArea = ({
    post_id,
    comments,
    inverted = false,
    setComments,
    onInputFocusChange,
    showLogin,
}: {
    post_id: number,
    comments: HeadlineComment[] | null,
    inverted?: boolean,
    setComments: React.Dispatch<React.SetStateAction<HeadlineComment[]>>,
    onInputFocusChange?: (isFocused: boolean) => void,
    showLogin?: () => void,
}) =>
{
    const userData = useContext(AuthContext);

    const [commentFocus, setCommentFocus] = useState(false);
    const [commentInput, setCommentInput] = useState("");

    if (!userData)
    {
        return (
            <div
                className={Wind.style({
                    padding: "p-4",
                    textAlign: "text-center",
                    fontSize: "text-sm",
                    lineHeight: "leading-5",
                    fontWeight: "font-normal",
                    color: "text-gray-500",
                    borderTopWidth: "border-t",
                    borderTopColor: inverted ? "border-t-[#3F3C39]" : "border-t-[#D9D9D9]",
                }).class}
            >
                <button
                    className={Wind.style({
                        display: "flex",
                        alignItems: "items-center",
                        justifyContent: "justify-center",
                        width: "w-full",
                        height: "h-9",
                        borderRadius: "rounded-full",
                        backgroundColor: inverted ? "bg-[#11100F]" : "bg-white",
                        borderWidth: "border",
                        borderColor: inverted ? "border-[#3F3C39]" : "border-[#D9D9D9]",
                        fontSize: "text-sm",
                        lineHeight: "leading-4",
                        color: inverted ? "text-white" : "text-black",
                    }).class}
                    onClick={(e) =>
                    {
                        e.stopPropagation();

                        if (showLogin)
                        {
                            showLogin();
                        }
                    }}
                >
                    Login to Comment
                </button>
            </div>
        );
    }

    return (
        <div
            className={Wind.style({
                borderTopColor:  inverted ? "border-t-[#3F3C39]" : "border-t-[#D9D9D9]",
                borderTopWidth: "border-t",
                paddingTop: "pt-4",
                transition: "transition-all ease-in-out",
                transitionDuration: "duration-300",
                paddingBottom: commentFocus
                    ? "pb-[calc(var(--safe-area-keyboard)_-_1rem)]" : "pb-4",
                paddingX: "px-4",
                display: "flex",
                flexDirection: "flex-row",
                gap: "gap-3",
            }).class}
        >
            <img
                src={userData.photo_path}
                alt="Your Avatar"
                className={Wind.style({
                    width: "w-9",
                    height: "h-9",
                    borderRadius: "rounded-full",
                    backgroundColor: "bg-poppy-yellow",
                }).class}
            />
            <input
                type="text"
                inputMode="text"
                placeholder="Add a comment"
                className={Wind.style({
                    flex: "flex-[1_1_100%]",
                    padding: "p-2",
                    borderRadius: "rounded-full",
                    backgroundColor: inverted ? "bg-[#11100F]" : "bg-white",
                    borderWidth: "border",
                    height: "h-9",
                    borderColor: inverted ? "border-[#3F3C39]" : "border-[#D9D9D9]",
                    fontSize: "text-sm",
                    lineHeight: "leading-4",
                    color: inverted ? "text-white" : "text-black",
                }).class}
                value={commentInput}
                onChange={(e) =>
                {
                    setCommentInput(e.target.value);
                }}

                onFocus={() =>
                {
                    if (onInputFocusChange)
                    {
                        onInputFocusChange(true);
                    }

                    setCommentFocus(true);
                }}

                onBlur={() =>
                {
                    if (onInputFocusChange)
                    {
                        onInputFocusChange(false);
                    }

                    // This is a workaround so the keyboard closing doesnt
                    // cause the comment popup to close as well
                    setTimeout(() =>
                    {
                        setCommentFocus(false);
                    }, 100);
                }}
                onClick={(e) =>
                {
                    e.stopPropagation();
                }}

                onKeyUp={(e) =>
                {
                    if (e.key === "Enter")
                    {
                        e.stopPropagation();


                        if (post_id === -1)
                        {
                            return;
                        }

                        social.postCommentOnSubmission(
                            post_id,
                            commentInput
                        ).then((data) =>
                        {
                            if (data && comments)
                            {
                                // Update the comments in the parent component
                                const newGroup = mergeComments(comments, data.comments);

                                setComments(newGroup);
                            }
                        })
                            .catch((err) =>
                            {
                                Logger.error(err);
                            });

                        setCommentInput("");
                    }
                }}
            />
        </div>
    );
};


CommentInputArea.displayName = "CommentInputArea";

const ANIMATION_TIME = 300;

export function DailyHeadlineCommentPopUp({
    viewingComments,
    className,
    inverted = false,
    setViewingComments,
    setError,
}: {
    viewingComments: DailyHeadlineSubmissionPost | null,
    className?: string,
    inverted?: boolean,
    setViewingComments: React.Dispatch<React.SetStateAction<DailyHeadlineSubmissionPost | null>>,
    setError: React.Dispatch<React.SetStateAction<{ title: string, error: string } | null>>;
})
{
    const [loading, setLoading] = useState(false);
    const [viewingID, setViewingID] = useState<number | null>(null);
    const [comments, setComments] = useState<HeadlineComment[]>([]);


    useEffect(() =>
    {
        if (viewingComments)
        {
            setViewingID(viewingComments.id);

            if (viewingID === viewingComments.id)
            {
                return;
            }

            setLoading(true);

            social.getCommentsForSubmission(viewingComments.id, 0).then((data) =>
            {
                if (data)
                {
                    const newGroup = mergeComments(comments, data.comments);

                    setComments(newGroup);
                }

                setLoading(false);
            })
                .catch((err) =>
                {
                    Logger.error(err);

                    setError({
                        title: "Error",
                        error: "Failed to load comments.",
                    });

                    setLoading(false);
                });
        }
    }, [viewingComments, viewingID, setError, comments]);

    useEffect(() =>
    {
        if (viewingComments)
        {
            setCommentTransition(true);
            setCommentTransitionDirection("in");
            setComments([]);

            // if (scrollAreaRef.current)
            // {
            //     scrollAreaRef.current.scrollTo({
            //         top: scrollAreaRef.current.scrollHeight
            //                              + scrollAreaRef.current.clientHeight + 100,
            //         behavior: "smooth",
            //     });
            // }
        }
        else
        {
            setViewingID(null);
            setCommentTransition(false);
            setCommentTransitionDirection("out");
        }
    }, [setError, setComments, viewingComments]);

    useEffect(() =>
    {
        setComments([]);
    }, [viewingComments]);

    const scrollAreaRef = useRef<HTMLDivElement>(null);

    const [commentTransition, setCommentTransition] = useState(false);
    const [commentTransitionDirection, setCommentTransitionDirection] = useState<"in" | "out">("in");
    const [isFocused, setIsFocused] = useState(false);
    const [showLogin, setShowLogin] = useState(false);

    return (
        <>
            <Swipeable
                className={twMerge(Wind.style({
                    position: "fixed",
                    inset: "inset-0",
                    bottom: "bottom-0",
                    left: "left-0",
                    right: "right-0",
                    top: "top-0",
                    backgroundColor: "bg-black/50",
                    backdropBlur: "backdrop-blur-sm",
                    zIndex: "z-[100]",
                    display: "flex",
                    flexDirection: "flex-col",
                    alignItems: "items-end",

                    transition: "transition-all ease-in-out",
                    transitionDuration: "duration-300",
                    opacity: commentTransition
                        ? commentTransitionDirection === "in"
                            ? "opacity-100"
                            : "opacity-0"
                        : "opacity-0",

                    pointerEvents: commentTransition
                        ? commentTransitionDirection === "in"
                            ? "pointer-events-auto"
                            : "pointer-events-none"
                        : "pointer-events-none",
                }).class, className)}
                handlers={{
                    onSwipedDown: () =>
                    {
                        if (!isFocused)
                        {
                            setCommentTransitionDirection("out");

                            setTimeout(() =>
                            {
                                setViewingComments(null);
                            }, ANIMATION_TIME * 2);

                            return;
                        }
                        else
                        {
                            Keyboard.hide().catch((err) =>
                            {
                                Logger.error("Failed to hide keyboard:", err);
                            });
                        }
                    },
                    onTap: (e) =>
                    {
                        const target = e.event.target as HTMLDivElement;

                        if (e.event.target === e.event.currentTarget || target.id === "comment-popup")
                        {
                            e.event.preventDefault();
                            e.event.stopPropagation();
                            setCommentTransitionDirection("out");

                            setTimeout(() =>
                            {
                                setViewingComments(null);
                            }, ANIMATION_TIME * 2);
                        }
                    }
                }}
            >
                <Padding
                    top
                >
                    <div
                        className={Wind.style({
                            position: "relative",
                            width: "w-full",
                            height: "h-full",
                            display: "flex",
                            flexDirection: "flex-col",
                            alignItems: "items-stretch",
                            justifyContent: "justify-end",
                        }).class}
                        id="comment-popup"
                    >
                        <div
                            className={Wind.style({
                                backgroundColor: inverted ? "bg-[#11100F]" : "bg-white",

                                display: "flex",
                                flexDirection: "flex-col",
                                borderTopRadius: "rounded-t-3xl",
                                maxHeight: "max-h-[calc(100vh_-_var(--safe-area-bottom)_-_4rem)]",
                                paddingBottom: "pb-[--safe-area-bottom]",
                                transformGPU: "transform-gpu",
                                transition: "transition-all ease-in-out",
                                transitionDuration: "duration-300",
                                transformTranslateY: commentTransition
                                    ? commentTransitionDirection === "in"
                                        ? "translate-y-0"
                                        : "translate-y-full"
                                    : "translate-y-full",
                                opacity: commentTransition
                                    ? commentTransitionDirection === "in"
                                        ? "opacity-100"
                                        : "opacity-0"
                                    : "opacity-0",
                            }).class}
                        >
                            <div
                                className={Wind.style({
                                    display: "flex",
                                    flexDirection: "flex-col",
                                    padding: "p-4",
                                    borderBottomColor: inverted ? "border-b-[#3F3C39]" : "border-b-[#D9D9D9]",
                                    borderBottomWidth: "border-b",
                                    textAlign: "text-center",
                                    alignItems: "items-center",
                                    justifyContent: "justify-center",
                                    position: "relative",
                                    color: inverted ? "text-white" : "text-black",
                                }).class}
                            >
                                <h1
                                    className={Wind.style({
                                        fontFamily: "font-poppins",
                                        fontSize: "text-base",
                                        lineHeight: "leading-5",
                                        fontWeight: "font-normal",
                                        display: "block",
                                    }).class}
                                >
                                    Comments
                                </h1>
                                <button
                                    onClick={(e) =>
                                    {
                                        e.stopPropagation();
                                        setViewingComments(null);
                                    }}
                                >
                                    <CloseIcon
                                        className={Wind.style({
                                            width: "w-8",
                                            height: "h-8",
                                            position: "absolute",
                                            right: "right-4",
                                            top: "top-1/2",
                                            transformTranslateY: "-translate-y-1/2",
                                        }).class}
                                    />
                                </button>
                            </div>
                            <div
                                className={Wind.style({
                                    flex: "flex-[1_1_100%]",
                                    overflowY: "overflow-y-auto",
                                }).class}
                                ref={scrollAreaRef}
                                onScroll={() =>
                                {
                                    if (scrollAreaRef.current)
                                    {
                                        const scrollTop = scrollAreaRef.current.scrollTop;
                                        const scrollHeight = scrollAreaRef.current.scrollHeight;
                                        const clientHeight = scrollAreaRef.current.clientHeight;

                                        // Check if the user is at the bottom of the scrollable area
                                        const isAtBottom = scrollHeight - scrollTop <= clientHeight + 10;

                                        if (isAtBottom && !loading)
                                        {
                                            Logger.log("Loading more comments...");

                                            const lastComment = comments[
                                                comments.length - 1
                                            ];

                                            if (viewingComments)
                                            {
                                                setViewingID(viewingComments.id);

                                                if (comments.length >= viewingComments.stats.comments)
                                                {
                                                    return;
                                                }

                                                setLoading(true);

                                                social.getCommentsForSubmission(
                                                    viewingComments.id,
                                                    lastComment.id
                                                ).then((data) =>
                                                {
                                                    if (data)
                                                    {
                                                        const newGroup = mergeComments(comments, data.comments);

                                                        setComments(newGroup);
                                                    }

                                                    setLoading(false);
                                                })
                                                    .catch((err) =>
                                                    {
                                                        Logger.error(err);

                                                        setError({
                                                            title: "Error",
                                                            error: "Failed to load comments.",
                                                        });

                                                        setLoading(false);
                                                    });
                                            }
                                        }
                                    }
                                }}
                            >
                                {comments.map((comment) =>
                                {
                                    return (
                                        <DailyHeadlineComment
                                            inverted={inverted}
                                            key={comment.id}
                                            comment={comment}
                                            setError={setError}
                                        />
                                    );
                                })
                                }
                                {loading && (
                                    <div
                                        className={Wind.style({
                                            display: "flex",
                                            justifyContent: "justify-center",
                                            alignItems: "items-center",
                                            marginTop: "-mt-20",
                                            marginBottom: "mb-4",
                                            padding: "p-4",
                                            backgroundColor: inverted ? "bg-[#11100F]" : "bg-white",
                                            borderRadius: "rounded-xl",
                                            width: "w-full",
                                            height: "h-24",
                                            borderWidth: "border",
                                            borderColor: inverted ? "border-[#3F3C39]" : "border-[#D9D9D9]",
                                            borderStyle: "border-dashed",
                                            borderTopWidth: "border-t-2",
                                            borderBottomWidth: "border-b-2",
                                            borderLeftWidth: "border-l-2",
                                            borderRightWidth: "border-r-2",
                                        }).class}
                                    >
                                        <RealizeLoader className="w-24 h-24" />
                                    </div>
                                )}
                                {comments.length === 0 && (
                                    <div>
                                        <h1
                                            className={Wind.style({
                                                fontFamily: "font-poppins",
                                                fontSize: "text-base",
                                                lineHeight: "leading-5",
                                                fontWeight: "font-normal",
                                                display: "block",
                                                textAlign: "text-center",
                                                paddingY: "py-4",
                                                fontStyle: "italic",
                                                color: "text-gray-500",
                                            }).class}
                                        >
                                            No comments yet.
                                        </h1>
                                    </div>
                                )}
                            </div>
                            <CommentInputArea
                                inverted={inverted}
                                post_id={viewingComments?.id ?? -1}
                                comments={comments}
                                onInputFocusChange={(isFocused) =>
                                {
                                    setIsFocused(isFocused);

                                    if (isFocused)
                                    {
                                        setCommentTransitionDirection("in");
                                    }
                                }}
                                setComments={(newComments) =>
                                {
                                    setComments(newComments);


                                    setTimeout(() =>
                                    {
                                        if (scrollAreaRef.current)
                                        {
                                            scrollAreaRef.current.scrollTo({
                                                top: 0,
                                                behavior: "smooth",
                                            });
                                        }
                                    }, MS_IN_S / 10);
                                }}
                                showLogin={() =>
                                {
                                    setShowLogin(true);
                                }}
                            />
                        </div>
                    </div>
                </Padding>
            </Swipeable>
            {showLogin && (
                <PopUpLogin
                    onClose={() =>
                    {
                        setShowLogin(false);
                    }}
                />
            )}
        </>
    );
}

export { CommentInputArea };

export default DailyHeadlineCommentPopUp;
